/* eslint-disable max-lines-per-function */
import CardWrapper from '../wrapper/card';
import CustomInput from '../reusable/input';
import { EditProfileParams } from '../../params/auth';
import { editProfileThunk } from '../../redux/thunks';
import ImageClass from '../../assets';
import { settingFlagDetails } from '../../redux/slices';
import { UpdateProfileSchema } from '../../schema/profile_schema';
import { AppDispatch, RootState } from '../../redux/store';
import { Avatar, Box, Button, Divider, Typography } from '@mui/material';
import { Form, Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';

const EditProfileComponent = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { currentUser } = useSelector((state: RootState) => state.app);
    const initialValues: EditProfileParams = {
        file: '',
        first_name: currentUser?.first_name || '',
        middle_name: currentUser?.middle_name || '',
        last_name: currentUser?.last_name || '',
        address: currentUser?.address || '',
        country: currentUser?.country || '',
        zip_code: currentUser?.zip_code || '',
        email: currentUser?.email || '',
        phone_number: currentUser?.phone_number || '',
        organization_name: currentUser?.agency_name || '',
        state: currentUser?.state || '',
        city: ''
    };

    const onSubmitHandler = async (values: EditProfileParams) => {
        try {
            await dispatch(editProfileThunk(values)).unwrap();
            const data = {
                isEditing: false,
                isUpdatingPassword: false
            };
            dispatch(settingFlagDetails(data));
        } catch (e) {
            //
        }
    };

    const onCancelHandler = () => {
        const data = {
            isEditing: false,
            isUpdatingPassword: false
        };
        dispatch(settingFlagDetails(data));
    };

    const convertToBase64 = (file: File, setFieldValue: (field: string, value: string | ArrayBuffer | null) => void) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
            setFieldValue('file', reader.result);
        };
        reader.onerror = (error) => {
            console.error('Base64 Conversion Error: ', error);
        };
    };

    return <Box p={2}>
        <CardWrapper minHeight="85vh" overflow="auto">
            <Formik initialValues={initialValues} validationSchema={UpdateProfileSchema} onSubmit={onSubmitHandler}>
                {({ values, handleChange, handleBlur, errors, touched, setFieldValue }) => (
                    <Form>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography variant="h4" color="textSecondary">Update Profile</Typography>
                            <Box display="flex" gap={1}>
                                <Button variant="contained" type="submit" >Save Changes</Button>
                                <Button variant="outlined" color="secondary" onClick={onCancelHandler}>Cancel</Button>
                            </Box>
                        </Box>
                        <CardWrapper>
                            <Box display="flex" gap={2} mb={2}>
                                <Box>
                                    <Avatar
                                        src={values.file || ImageClass.userProfile}
                                        alt="User Profile"
                                        sx={{ width: 100, height: 100, borderRadius: '10px' }}
                                    />
                                </Box>
                                <Box display="flex" flexDirection="column" justifyContent="center" gap={2}>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        style={{ display: 'none' }}
                                        id="upload-photo"
                                        onChange={(e) => {
                                            if (e.target.files && e.target.files[0]) {
                                                convertToBase64(e.target.files[0], setFieldValue);
                                            }
                                        }}
                                    />
                                    <label htmlFor="upload-photo">
                                        <Button variant="outlined" component="span">
                                            Upload new photo
                                        </Button>
                                    </label>
                                    <Typography variant="subtitle1" color="textDisabled">
                                        Allowed JPG, GIF or PNG. Max size of 800MB
                                    </Typography>
                                </Box>
                            </Box>
                            <Divider />
                            <Box display="flex" flexDirection="column" gap={2} my={2}>
                                <Box>
                                    <Typography variant="subtitle1" color="textDisabled">PERSONAL INFO</Typography>
                                    <Box display="flex" gap={2}>
                                        <CustomInput
                                            label="First Name"
                                            placeholder="First Name"
                                            value={values.first_name}
                                            onChange={handleChange('first_name')}
                                            onBlur={handleBlur('first_name')}
                                            error={touched.first_name && errors.first_name}
                                            fullWidth
                                        />
                                        <CustomInput
                                            label="Middle Name"
                                            placeholder="Middle Name"
                                            value={values.middle_name}
                                            onChange={handleChange('middle_name')}
                                            onBlur={handleBlur('middle_name')}
                                            error={touched.middle_name && errors.middle_name}
                                            fullWidth
                                        />
                                        <CustomInput
                                            label="Last Name"
                                            placeholder="Last Name"
                                            value={values.last_name}
                                            onChange={handleChange('last_name')}
                                            onBlur={handleBlur('last_name')}
                                            error={touched.last_name && errors.last_name}
                                            fullWidth
                                        />
                                    </Box>
                                </Box>
                                <Box>
                                    <Typography variant="subtitle1" color="textDisabled">ADDRESS</Typography>
                                    <Box display="flex" gap={2} mb={1}>
                                        <CustomInput
                                            label="State"
                                            placeholder="State"
                                            value={values.state}
                                            onChange={handleChange('state')}
                                            onBlur={handleBlur('state')}
                                            error={touched.state && errors.state}
                                            fullWidth
                                        />
                                        <CustomInput
                                            label="City"
                                            placeholder="City"
                                            value={values.city}
                                            onChange={handleChange('city')}
                                            onBlur={handleBlur('city')}
                                            error={touched.city && errors.city}
                                            fullWidth
                                        />
                                        <CustomInput
                                            label="Country"
                                            placeholder="Country"
                                            value={values.country}
                                            onChange={handleChange('country')}
                                            onBlur={handleBlur('country')}
                                            error={touched.country && errors.country}
                                            fullWidth
                                        />
                                        <CustomInput
                                            label="Zip Code"
                                            placeholder="Zip Code"
                                            value={values.zip_code}
                                            onChange={handleChange('zip_code')}
                                            onBlur={handleBlur('zip_code')}
                                            error={touched.zip_code && errors.zip_code}
                                            fullWidth
                                        />
                                    </Box>
                                    <CustomInput
                                        type="textarea"
                                        placeholder="Address"
                                        value={values.address}
                                        onChange={handleChange('address')}
                                        onBlur={handleBlur('address')}
                                        error={touched.address && errors.address}
                                        fullWidth
                                    />
                                </Box>
                                <Box>
                                    <Typography variant="subtitle1" color="textDisabled">CONTACTS</Typography>
                                    <Box display="flex" gap={2}>
                                        <CustomInput
                                            label="Email"
                                            placeholder="Email"
                                            value={values.email}
                                            onChange={handleChange('email')}
                                            onBlur={handleBlur('email')}
                                            error={touched.email && errors.email}
                                            fullWidth
                                        />
                                        <CustomInput
                                            type="phone"
                                            value={values.phone_number}
                                            onChangeForPhone={handleChange('phone_number')}
                                            onBlur={handleBlur('phone_number')}
                                            error={touched.phone_number && errors.phone_number}
                                            fullWidth
                                            label="Mobile No."
                                            placeholder="201-555-0123"
                                            variant="outlined"
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        </CardWrapper>
                    </Form>
                )}
            </Formik>
        </CardWrapper>
    </Box>;
};

export default EditProfileComponent;